/**
 * Tipos e chamadas do estoque em grade.
 *
 * O contrato espelha `api/internal/modulos/estoque/dominio.go`. Um detalhe
 * carrega significado: `Celula.existe` é diferente de `saldo === 0`. A
 * primeira é "esta cor não vem neste tamanho", a segunda é "veio e acabou".
 * Confundir as duas manda comprar o que o fornecedor não fabrica.
 */
import { api } from './api'
import type { Centavos } from './dinheiro'

export type Tipo =
  | 'compra'
  | 'venda'
  | 'devolucao'
  | 'ajuste'
  | 'inventario'
  | 'transferencia_saida'
  | 'transferencia_entrada'

export type Motivo = 'perda' | 'sobra' | 'uso_interno' | 'correcao'
export type Sentido = 'entrada' | 'saida'

export type Filial = {
  codigo: string
  nome: string
  principal: boolean
}

export type Celula = {
  existe: boolean
  tamanho: string
  codigo_barras?: string
  saldo: number
  estoque_minimo: number
}

export type CorDaGrade = {
  cor: string
  hex?: string
  celulas: Celula[]
  total: number
}

export type ProdutoGrade = {
  codigo: string
  nome: string
  categoria: string
  tamanhos: string[]
  cores: CorDaGrade[]
  pecas: number
  minimo: number
  valor_custo_centavos: Centavos
}

export type ResumoEstoque = {
  pecas: number
  valor_custo_centavos: Centavos
  valor_venda_centavos: Centavos
  skus_zerados: number
  abaixo_do_minimo: number
  produtos: number
}

export type Grade = {
  filial: Filial
  filiais: Filial[]
  resumo: ResumoEstoque
  produtos: ProdutoGrade[]
}

export type LinhaMovimento = {
  tipo: Tipo
  tipo_rotulo: string
  codigo_barras: string
  produto: string
  cor: string
  tamanho: string
  quantidade: number
  filial: string
  responsavel: string
  motivo?: string
  observacao?: string
  autorizador?: string
  quando: string
}

export type Movimentos = {
  linhas: LinhaMovimento[]
  total: number
}

export type ResultadoAjuste = {
  codigo_barras: string
  produto: string
  cor: string
  tamanho: string
  saldo_anterior: number
  saldo_final: number
  movimento: number
  impacto_custo_centavos: Centavos
}

export type PedidoAjuste = {
  sku: string
  filial?: string
  quantidade: number
  motivo: Motivo
  sentido?: Sentido
  observacao?: string
}

/**
 * Os quatro motivos, com o sentido que cada um impõe.
 *
 * O sinal é do MOTIVO, não da tela — é o servidor que decide (ver
 * `SinalDoMotivo` no Go). Aqui a informação existe só para a tela poder
 * mostrar "−3 un" antes de enviar. Se a tela tentar mandar o sentido
 * contrário, a API recusa com 422 em vez de gravar o oposto.
 */
export const MOTIVOS: {
  chave: Motivo
  rotulo: string
  dica: string
  sentido: Sentido | 'ambos'
}[] = [
  {
    chave: 'perda',
    rotulo: 'Perda ou avaria',
    dica: 'Peça danificada, furto ou extravio',
    sentido: 'saida',
  },
  {
    chave: 'sobra',
    rotulo: 'Sobra encontrada',
    dica: 'Peça achada fora da contagem',
    sentido: 'entrada',
  },
  {
    chave: 'uso_interno',
    rotulo: 'Uso interno',
    dica: 'Vitrine, uniforme, brinde',
    sentido: 'saida',
  },
  {
    chave: 'correcao',
    rotulo: 'Correção de cadastro',
    dica: 'Erro de digitação em entrada anterior',
    sentido: 'ambos',
  },
]

export const FILTROS_MOVIMENTO: { chave: Tipo | ''; rotulo: string }[] = [
  { chave: '', rotulo: 'Tudo' },
  { chave: 'venda', rotulo: 'Vendas' },
  { chave: 'compra', rotulo: 'Entradas' },
  { chave: 'ajuste', rotulo: 'Ajustes' },
  { chave: 'devolucao', rotulo: 'Devoluções' },
  { chave: 'inventario', rotulo: 'Inventário' },
]

export function carregarGrade(
  filial: string,
  busca: string,
  sinal?: AbortSignal,
): Promise<Grade> {
  const params = new URLSearchParams()
  if (filial) params.set('filial', filial)
  if (busca) params.set('busca', busca)
  return api.get<Grade>(`/estoque/grade?${params}`, sinal)
}

export function carregarMovimentos(
  filial: string,
  tipo: Tipo | '',
  sinal?: AbortSignal,
): Promise<Movimentos> {
  const params = new URLSearchParams()
  if (filial) params.set('filial', filial)
  if (tipo) params.set('tipo', tipo)
  return api.get<Movimentos>(`/estoque/movimentos?${params}`, sinal)
}

export function lancarAjuste(pedido: PedidoAjuste, pin?: string): Promise<ResultadoAjuste> {
  return api.post<ResultadoAjuste>('/estoque/ajustes', pedido, pin)
}

/**
 * Classifica o saldo de uma posição para a cor do badge.
 *
 * Zero é vermelho, no mínimo ou abaixo é âmbar, acima é verde. Os limites vêm
 * do `estoque_minimo` do SKU, não de um número fixo na tela: loja de tênis
 * trabalha mínimo 1 por numeração e loja de camiseta trabalha 4.
 */
export function riscoDaCelula(celula: Celula): 'ausente' | 'zerado' | 'baixo' | 'ok' {
  if (!celula.existe) return 'ausente'
  if (celula.saldo === 0) return 'zerado'
  if (celula.saldo <= celula.estoque_minimo) return 'baixo'
  return 'ok'
}
